import { createSignal, createEffect, For, Show, onCleanup } from 'solid-js';
import { A, useNavigate } from '@solidjs/router';
import { useSFetch } from '../hooks/sfetch';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import Text from '../components/Text';
import styles from './Tenants.module.css';
import { firstError } from '../api/envelope';

export default function AdminNewTenant() {
    const sfetch = useSFetch();
    const api = useApi();
    const toast = useToast();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [name, setName] = createSignal('');
    const [slug, setSlug] = createSignal('');
    const [ownerSearch, setOwnerSearch] = createSignal('');
    const [ownerResults, setOwnerResults] = createSignal<{ id: string; username: string }[]>([]);
    const [ownerUserId, setOwnerUserId] = createSignal('');
    const [creating, setCreating] = createSignal(false);

    let debounceHandle: number | undefined;
    createEffect(() => {
        const term = ownerSearch().trim();
        if (debounceHandle) window.clearTimeout(debounceHandle);
        if (!term || ownerUserId()) { setOwnerResults([]); return; }
        debounceHandle = window.setTimeout(async () => {
            const res = await sfetch('/api/User/Users');
            if (res.ok && res.value) {
                const matches = (res.value as { id: string; username: string }[])
                    .filter(u => u.username.toLowerCase().includes(term.toLowerCase()))
                    .slice(0, 10);
                setOwnerResults(matches);
            }
        }, 200);
    });
    onCleanup(() => { if (debounceHandle) window.clearTimeout(debounceHandle); });

    const valid = () => name().trim().length > 0 && slug().trim().length > 0 && ownerUserId().length > 0;

    function pickOwner(u: { id: string; username: string }) {
        setOwnerUserId(u.id);
        setOwnerSearch(u.username);
        setOwnerResults([]);
    }

    async function submit(e: Event) {
        e.preventDefault();
        if (!valid()) return;
        setCreating(true);
        try {
            const res = await api.tenantAdmin.createTenant({
                name: name().trim(),
                slug: slug().trim(),
                ownerUserId: ownerUserId(),
                defaultProfitMargin: 0,
                description: '',
                maxMembers: 0,
                allowMemberInvites: false,
            });
            if (isOk(res.data)) {
                toast?.info(t('TENANT_CREATED'));
                navigate('/admin/tenants');
            } else {
                toast?.error(firstError(res.data) ?? t('TENANT_CREATE_FAILED'));
            }
        } finally {
            setCreating(false);
        }
    }

    return (
        <div class={styles.formContainer}>
            <A href="/admin/tenants" class={styles.backLink}>
                <i class="fa-solid fa-arrow-left"></i>
                <Text val="ADMIN_TENANT_BACK_TO_LIST" />
            </A>

            <div class={styles.header}>
                <div class={styles.titleSection}>
                    <div class={styles.cardAvatar}>
                        <i class="fa-solid fa-building"></i>
                    </div>
                    <div class={styles.titleText}>
                        <h1><Text val="TENANT_CREATE" /></h1>
                        <p class={styles.subtitle}><Text val="ADMIN_TENANT_CREATE_SUBTITLE" /></p>
                    </div>
                </div>
            </div>

            <form class={styles.formCard} onSubmit={submit}>
                <label class={styles.formField}>
                    <span class={styles.fieldLabel}><Text val="TENANT_NAME" /></span>
                    <input
                        type="text"
                        class={styles.input}
                        placeholder={t('TENANT_NAME')}
                        value={name()}
                        onInput={e => setName(e.currentTarget.value)}
                    />
                </label>

                <label class={styles.formField}>
                    <span class={styles.fieldLabel}><Text val="TENANT_SLUG" /></span>
                    <input
                        type="text"
                        class={styles.input}
                        placeholder={t('TENANT_SLUG_PLACEHOLDER')}
                        value={slug()}
                        onInput={e => setSlug(e.currentTarget.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                    />
                    <span class={styles.fieldHelp}><Text val="TENANT_SLUG_HELP" /></span>
                </label>

                <div class={styles.formField}>
                    <span class={styles.fieldLabel}><Text val="ADMIN_TENANT_OWNER" /></span>
                    <input
                        type="text"
                        class={styles.input}
                        placeholder={t('TENANT_SEARCH_USERNAME')}
                        value={ownerSearch()}
                        onInput={e => {
                            setOwnerSearch(e.currentTarget.value);
                            setOwnerUserId('');
                        }}
                    />
                    <Show when={ownerResults().length > 0}>
                        <div class={styles.searchResults}>
                            <For each={ownerResults()}>
                                {u => (
                                    <div
                                        class={`${styles.searchResult}${ownerUserId() === u.id ? ` ${styles.selected}` : ''}`}
                                        onClick={() => pickOwner(u)}
                                    >
                                        <i class="fa-solid fa-user"></i>
                                        <span>{u.username}</span>
                                    </div>
                                )}
                            </For>
                        </div>
                    </Show>
                    <Show when={ownerUserId()}>
                        <span class={styles.fieldHelp}>
                            <Text val="TENANT_SELECTED" /> {ownerSearch()}
                        </span>
                    </Show>
                </div>

                <div class={styles.formActions}>
                    <A href="/admin/tenants" class="btn btn-secondary"><Text val="CANCEL" /></A>
                    <button type="submit" class="btn btn-primary" disabled={!valid() || creating()}>
                        <Show when={creating()} fallback={<i class="fa-solid fa-plus"></i>}>
                            <i class="fa-solid fa-spinner fa-spin"></i>
                        </Show>
                        <Text val={creating() ? 'CREATING' : 'TENANT_CREATE'} />
                    </button>
                </div>
            </form>
        </div>
    );
}
